import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@reckon-web/box';
import { Text } from '@reckon-web/text';
import { makeId } from '@reckon-web/utils';
import {
  SortDescriptor,
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRow,
  createDefaultSort,
} from '@reckon-web/table';

import { DueDate } from '../../common/due-date';
import { TaskStatusBadge } from '../../common/task-status-badge';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import { Task } from '../../../hooks/use-query-tasks';
import { useOpenJobDialog } from '../../../hooks/use-open-job-dialog';
import { useOpenTaskDialog } from '../../../hooks/use-open-task-dialog';
import { TaskStatusEnum, TaskStatusEnumValue } from '../types/task-status-enum';
import { WorkItemTitle } from '../helpers';
import { useVisibleProperties } from '../../common/visible-properties';
import { JobStatusEnum } from '../../job/types/job-status-enum';

type ListViewProps = {
  tasks: Task[];
  testId?: string;
};

export const ListView = ({
  tasks,
  testId = `tasks__list-view`,
}: ListViewProps) => {
  const [{ taskList: taskListVisibleProperties }] = useVisibleProperties();
  const openJobDialog = useOpenJobDialog();
  const openTaskDialog = useOpenTaskDialog();

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'dueDate',
    direction: 'ascending',
  });

  const createSortHandler = useCallback(
    (column: string) => () => {
      setSortDescriptor((prevSort: SortDescriptor) => {
        const isActive = column === prevSort?.column;
        const direction =
          isActive && prevSort?.direction === 'ascending'
            ? 'descending'
            : 'ascending';
        return { column, direction };
      });
    },
    []
  );

  const getSortDirection = useCallback(
    (column: string) => {
      if (sortDescriptor) {
        return sortDescriptor.column === column
          ? sortDescriptor.direction
          : undefined;
      }
    },
    [sortDescriptor]
  );

  const sortedRows = useMemo(() => {
    if (sortDescriptor) {
      return tasks.sort(createDefaultSort(sortDescriptor));
    }
    return tasks;
  }, [tasks, sortDescriptor]);

  const taskStatusesToHideDueDaysFor = new Set<TaskStatusEnumValue>([
    TaskStatusEnum.valuesEnum.Cancelled,
    TaskStatusEnum.valuesEnum.Done,
  ]);

  return (
    <Box paddingBottom="xxlarge">
      <Table
        aria-label="jobs list view"
        data-testId={makeId(testId, 'sorted-data-table')}
      >
        <TableHeader stickyOffset={0}>
          <TableColumnHeader
            onClick={createSortHandler('job.client.name')}
            aria-sort={getSortDirection('job.client.name')}
          >
            debtor
          </TableColumnHeader>
          <TableColumnHeader
            onClick={createSortHandler('job.name')}
            aria-sort={getSortDirection('job.name')}
          >
            job
          </TableColumnHeader>
          <TableColumnHeader
            onClick={createSortHandler('name')}
            aria-sort={getSortDirection('name')}
          >
            tasks
          </TableColumnHeader>
          {taskListVisibleProperties.client && (
            <TableColumnHeader
              onClick={createSortHandler('entity.name')}
              aria-sort={getSortDirection('entity.name')}
            >
              client
            </TableColumnHeader>
          )}
          {taskListVisibleProperties.assigned && (
            <TableColumnHeader
              onClick={createSortHandler('assignee.fullName')}
              aria-sort={getSortDirection('assignee.fullName')}
            >
              assigned
            </TableColumnHeader>
          )}

          {taskListVisibleProperties.status && (
            <TableColumnHeader
              onClick={createSortHandler('statusOrderIndex')}
              aria-sort={getSortDirection('statusOrderIndex')}
            >
              status
            </TableColumnHeader>
          )}
          {taskListVisibleProperties.dueDate && (
            <TableColumnHeader
              onClick={createSortHandler('dueDate')}
              aria-sort={getSortDirection('dueDate')}
            >
              due date
            </TableColumnHeader>
          )}
          {taskListVisibleProperties.estimatedTime && (
            <TableColumnHeader
              onClick={createSortHandler('estimateHoursDisplay')}
              aria-sort={getSortDirection('estimateHoursDisplay')}
            >
              estimated time
            </TableColumnHeader>
          )}
        </TableHeader>

        <TableBody>
          {sortedRows.map((task) => {
            return (
              <TableRow key={task.cloudId}>
                {/* debtor */}
                <TableCell>{task.job.client?.name}</TableCell>

                {/* job */}
                <TableCell>
                  {task.job && (
                    <WorkItemTitle
                      name={task.job.name}
                      locked={
                        task.job.status.status ===
                        JobStatusEnum.valuesEnum.Closed
                      }
                      onClick={() => openJobDialog(task.job.cloudId)}
                      testId={makeId(testId, 'job')}
                      weight="regular"
                    />
                  )}
                </TableCell>

                {/* task */}
                <TableCell>
                  <WorkItemTitle
                    name={task.name}
                    locked={
                      task.job?.status.status ===
                      JobStatusEnum.valuesEnum.Closed
                    }
                    onClick={() => openTaskDialog(task.cloudId)}
                    weight="regular"
                    testId={makeId(testId, 'task')}
                  />
                </TableCell>

                {/* client */}
                {taskListVisibleProperties.client && (
                  <TableCell>{task.entity?.name}</TableCell>
                )}

                {/* assigned */}
                {taskListVisibleProperties.assigned && (
                  <TableCell>
                    {task.assignee && (
                      <NamedUserAvatar
                        user={task.assignee}
                        avatarSize="xsmall"
                        textSize="small"
                        testId={makeId(testId, 'assignee-avatar')}
                      />
                    )}
                  </TableCell>
                )}

                {/* status */}
                {taskListVisibleProperties.status && (
                  <TableCell>
                    {task.status && (
                      <TaskStatusBadge
                        status={task.status}
                        testId={makeId(testId, 'status-badge')}
                      />
                    )}
                  </TableCell>
                )}

                {/* due date */}
                {taskListVisibleProperties.dueDate && (
                  <TableCell>
                    {task.dueDate && (
                      <DueDate
                        date={task.dueDate}
                        showDueDays={Boolean(
                          task.status &&
                            !taskStatusesToHideDueDaysFor.has(task.status)
                        )}
                        testId={makeId(testId, 'due-date')}
                      />
                    )}
                  </TableCell>
                )}

                {/* estimated time */}
                {taskListVisibleProperties.estimatedTime && (
                  <TableCell>
                    {task.estimateHoursDisplay && (
                      <Text
                        size="small"
                        data={{ testid: makeId(testId, 'estimate') }}
                      >
                        {task.estimateHoursDisplay} hours
                      </Text>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
